import React, { useCallback, useContext, useState } from "react";
import { observer } from "mobx-react-lite";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { MessageType } from "src/components/notifications/Notifier";
import { GetCustomerUsers_customer_users as umsUser } from "src/api/generated/GetCustomerUsers";
import styles from "./CopyUserButton.module.css";
import { getRoleKey } from "src/network/User";
import { NetworkConfig } from "src/network/NetworkConfig";
import Axios from "src/network/Axios";
import { ENUM_TYPE } from "src/stores/EnumerationStore";

interface UserResetButtonProps {
    user: umsUser;
    userid: string;
}

interface CopyUserResponse {
    username: string;
    password: string;
}

const CopyUserButtonBase = (props: UserResetButtonProps) => {
    const { t } = useTranslation();
    const { uiStore, authStore, enumerationsStore } = useContext(RootStoreContext);

    const [isUserResetModalDisplaying, setIsUserResetModalDisplaying] = useState(false);
    const [copyUserResponse, setCopyUserResponse] = useState<CopyUserResponse | null>(null);
    const [copyButton, setCopyButton] = useState(t("screens.customer_users.copying").toString());
    const [showWarning, setShowWarning] = useState(false);

    const openCopyUserModal = useCallback(async () => {
        const user = props.user;

        if (authStore.user && user && props.userid) {
            try {
                setShowWarning(false);
                setCopyUserResponse(null);

                const response = await Axios.getAuthInstance().post(
                    NetworkConfig.copyUser,
                    { id: props.userid },
                    {
                        headers: {
                            "x-hasura-role": getRoleKey(authStore.user.role)
                        }
                    }
                );

                setCopyUserResponse(response.data);
                setIsUserResetModalDisplaying(true);
            } catch (error: any) {
                const errorMessage = `${t("screens.customer_users.error_copy_user")}: ${error}`;
                uiStore.printStatusMessage(errorMessage, MessageType.ERROR);
                console.error(errorMessage);
            }
        }
    }, [authStore.user, props.user, props.userid, t, uiStore]);

    // first attempt: show warning and do NOT close
    const requestCloseCopyUserModal = useCallback(() => {
        if (!showWarning) {
            setShowWarning(true);
            return;
        }

        // second close attempt
        setIsUserResetModalDisplaying(false);
        setShowWarning(false);
    }, [showWarning]);

    const copyPasswordToClipboard = useCallback(() => {
        const elem = document.createElement("textarea");
        elem.value = copyUserResponse?.password ?? "";
        document.body.appendChild(elem);
        elem.select();
        document.execCommand("copy");
        document.body.removeChild(elem);

        setCopyButton(t("screens.customer_users.saved_to_clipboard").toString());

        setTimeout(() => {
            setCopyButton(t("screens.customer_users.copying").toString());
        }, 3000);
    }, [t, copyUserResponse?.password]);

    const isUserRegisterd = !props.user.registered || false;

    const hasValidRole = props.user.userroles.some((r) => [31, 32, 33, 36].includes(r.role));
    if (!hasValidRole) return null;

    const userRolesMap = enumerationsStore.getEnumMapByType(ENUM_TYPE.USER_ROLES);
    const roleLabels = props.user.userroles
        .map(({ role }) => userRolesMap.get(role)?.label)
        .filter((label): label is string => Boolean(label))
        .join(", ");

    return (
        <>
            <UI.Button
                label={t("screens.customer_users.login_as")}
                className={styles.CopyUserButton}
                onClick={openCopyUserModal}
                disabled={isUserRegisterd}
                variant={isUserRegisterd ? "secondary" : "primary"}
            />

            <UI.Modal
                backdrop={"static"}
                title={t("screens.customer_users.copy_user_title").toString()}
                show={isUserResetModalDisplaying}
                onClose={requestCloseCopyUserModal}
                className={styles.ModalCopyUser}
                size="lg"
            >
                <div className="mb-4">
                    {showWarning ? (
                        <div className={styles.WarningText}>{t("screens.customer_users.warning_text")}</div>
                    ) : null}

                    <div className={styles.Name}>{props.user.name1 + " " + props.user.name2}</div>

                    <div className={styles.InfoRow}>
                        <div className={styles.InfoLabel}>Rolle:</div>
                        <div className={styles.InfoKey}>{roleLabels}</div>
                    </div>

                    <div className={styles.InfoRow}>
                        <div className={styles.InfoLabel}>Benutzer:</div>
                        <div className={styles.InfoKey}>{copyUserResponse?.username}</div>
                    </div>

                    <div className={styles.InfoRowPassword}>
                        <div className={styles.InfoLabel}>Passwort:</div>
                        <div className={styles.InfoKey}>
                            {copyUserResponse?.password}
                            {copyUserResponse?.password ? (
                                <div className={"d-flex align-items-center mt-2"}>
                                    <UI.Icon
                                        icon={UI.SVGIcon.Documents}
                                        style={{ cursor: "pointer" }}
                                        onClick={copyPasswordToClipboard}
                                    />
                                    <div>
                                        <span className={styles.CopyButtonText}>{copyButton}</span>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </UI.Modal>
        </>
    );
};

export const CopyUserButton = observer(CopyUserButtonBase);
